import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Check, Calendar, MapPin, Phone, Mail, Download, ChevronLeft, Loader2 } from 'lucide-react';
import { WellnessNav, WellnessFooter } from './WellnessLanding';
import { WELLNESS_API, fmtUSD } from './_shared';

export default function WellnessConfirmation() {
  const { code } = useParams();
  const nav = useNavigate();
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${WELLNESS_API}/bookings/${code}`);
        setB(r.data);
      } catch {
        setB({ error: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [code]);

  return (
    <div className="min-h-screen bg-[#faf7ef] text-[#2a2620]" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }} data-testid="confirmation-page">
      <WellnessNav />
      <div className="max-w-3xl mx-auto px-6 py-12">
        {loading && (
          <div className="text-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#2f5d50] mx-auto" /></div>
        )}
        {!loading && b?.error && (
          <div className="text-center py-20">
            <p className="text-lg font-bold mb-2">Booking not found</p>
            <p className="text-sm text-[#6b6358] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>The confirmation code <code className="bg-[#f3ece0] px-1 rounded">{code}</code> does not match any booking.</p>
            <button onClick={() => nav('/wellness')} className="mt-4 px-5 py-2 rounded-full bg-[#2f5d50] text-white text-sm font-semibold">Back to home</button>
          </div>
        )}
        {!loading && b && !b.error && (
          <>
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-[#2f5d50] flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold">Booking confirmed</h1>
              <p className="mt-3 text-sm text-[#6b6358] font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                Confirmation code
              </p>
              <p className="text-2xl font-bold text-[#c89b3c] tracking-wider" data-testid="confirmation-code">{b.confirmation_code}</p>
              <p className="text-xs text-[#6b6358] mt-2 font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>A confirmation email has been queued to <strong>{b.guest?.email}</strong>.</p>
            </div>

            <div className="bg-white rounded-2xl border border-[#e0d6c2] shadow-sm overflow-hidden mb-6">
              <div className="bg-[#2f5d50] text-white px-6 py-4">
                <p className="text-[10px] uppercase tracking-widest opacity-80 font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>Your stay</p>
                <h2 className="text-xl font-bold">{b.room_name}</h2>
              </div>
              <div className="px-6 py-5 font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  <Info icon={Calendar} label="Check-in">{b.check_in}</Info>
                  <Info icon={Calendar} label="Check-out">{b.check_out}</Info>
                  <Info icon={Calendar} label="Nights">{b.nights}</Info>
                  <Info icon={Calendar} label="Guests">{b.guests}</Info>
                </div>

                <Divider />

                <p className="text-[10px] uppercase tracking-widest text-[#c89b3c] font-semibold mb-3">Treatments</p>
                {(b.treatments_summary || []).length === 0 && <p className="text-xs text-[#6b6358]">No treatments selected. You can add them on arrival.</p>}
                {(b.treatments_summary || []).map((t) => (
                  <div key={t.id} className="flex justify-between text-sm py-1.5">
                    <span>{t.name}</span>
                    <span className="text-[#6b6358]">{t.days_billed} days · {fmtUSD(t.subtotal)}</span>
                  </div>
                ))}

                <Divider />

                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-[#6b6358]">Room ({b.nights} nights × {fmtUSD(b.room_price_per_night)})</span><span>{fmtUSD(b.room_total)}</span></div>
                  <div className="flex justify-between"><span className="text-[#6b6358]">Treatments</span><span>{fmtUSD(b.treatments_total)}</span></div>
                  <div className="flex justify-between pt-3 mt-3 border-t border-[#e0d6c2] text-lg font-bold text-[#2f5d50]">
                    <span>Grand total</span>
                    <span>{fmtUSD(b.grand_total)}</span>
                  </div>
                  <p className="text-[11px] text-[#c89b3c] font-semibold mt-2">Payment: Pay at arrival (cash, card or international transfer)</p>
                </div>
              </div>
            </div>

            <div className="bg-[#f3ece0] rounded-2xl border border-[#e0d6c2] p-5 font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
              <p className="text-[10px] uppercase tracking-widest text-[#c89b3c] font-semibold mb-3">What happens next</p>
              <ul className="text-sm space-y-2 text-[#2a2620]">
                <li>1. Our care team will email you a welcome packet within 24 hours.</li>
                <li>2. {b.consultation?.requested ? 'Your pre-arrival video consultation will be scheduled.' : 'Optional pre-arrival video consultation available — reply to confirm.'}</li>
                <li>3. On the day of check-in, please arrive between 12pm and 6pm local time (Hyderabad, IST).</li>
                <li>4. A chauffeur pickup from RGIA Hyderabad airport can be arranged on request ($25 extra).</li>
              </ul>
              <div className="mt-4 pt-4 border-t border-[#e0d6c2] text-xs text-[#6b6358] space-y-1">
                <p><MapPin className="w-3 h-3 inline" /> Dr. Raju&apos;s Institute of Ayurveda, 8/19 Bahar-A, Sahara States, Mansoorabad, L.B. Nagar, Hyderabad 500068</p>
                <p><Phone className="w-3 h-3 inline" /> Instagram: @drraju108 · YouTube: Dr. Raju Ayurveda</p>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap justify-center gap-3 font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
              <button onClick={() => window.print()} className="px-5 py-2.5 rounded-full bg-white border border-[#2f5d50] text-[#2f5d50] text-sm font-semibold flex items-center gap-2 hover:bg-[#f3ece0] transition" data-testid="print-receipt">
                <Download className="w-4 h-4" />Print / Save Receipt
              </button>
              <button onClick={() => nav('/wellness')} className="px-5 py-2.5 rounded-full text-[#2f5d50] text-sm font-semibold flex items-center gap-2 hover:bg-[#f3ece0] transition" data-testid="back-home">
                <ChevronLeft className="w-4 h-4" />Back to home
              </button>
            </div>
          </>
        )}
      </div>
      <WellnessFooter />
    </div>
  );
}

function Info({ icon: Icon, label, children }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-[#6b6358] font-semibold">{label}</p>
      <p className="text-sm font-bold mt-1 flex items-center gap-1.5"><Icon className="w-3.5 h-3.5 text-[#2f5d50]" />{children}</p>
    </div>
  );
}
function Divider() { return <div className="my-5 h-px bg-[#e0d6c2]" />; }
